import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
/**
 * Material Textarea Autosize and helper methods
 * See https://mui.com/base-ui/react-textarea-autosize/
 * Code copied from https://github.com/mui/material-ui/tree/master/packages/mui-base/src/TextareaAutosize
 */
import debounce from './debounce';
import ownerWindow from './owner-window';
import useEnhancedEffect from './use-enhanced-effect';
import useForkRef from './use-fork-ref';
function getStyleValue(value) {
    return parseInt(value, 10) || 0;
}
const styles = {
    shadow: {
        // Visibility needed to hide the extra text area on iPads
        visibility: 'hidden',
        // Remove from the content flow
        position: 'absolute',
        // Ignore the scrollbar width
        overflow: 'hidden',
        height: 0,
        top: 0,
        left: 0,
        // Create a new layer, increase the isolation of the computed values
        transform: 'translateZ(0)',
    },
};
function isEmpty(obj) {
    return (obj === undefined ||
        obj === null ||
        Object.keys(obj).length === 0 ||
        (obj.outerHeightStyle === 0 && !obj.overflowing));
}
export const TextareaAutosize = (props) => {
    const { onChange, maxRows, minRows = 1, style, value, ref, ...other } = props;
    const { current: isControlled } = React.useRef(value != null);
    const inputRef = React.useRef(null);
    const handleRef = useForkRef(ref, inputRef);
    const heightRef = React.useRef(null);
    const shadowRef = React.useRef(null);
    const calculateTextareaStyles = React.useCallback(() => {
        const input = inputRef.current;
        const containerWindow = ownerWindow(input);
        const computedStyle = containerWindow.getComputedStyle(input);
        // If input's width is shrunk and it's not visible, don't sync height.
        if (computedStyle.width === '0px') {
            return {
                outerHeightStyle: 0,
                overflowing: false,
            };
        }
        const inputShallow = shadowRef.current;
        inputShallow.style.width = computedStyle.width;
        inputShallow.value = input.value || props.placeholder || 'x';
        if (inputShallow.value.slice(-1) === '\n') {
            // Certain fonts which overflow the line height will cause the textarea
            // to report a different scrollHeight depending on whether the last line
            // is empty. Make it non-empty to avoid this issue.
            inputShallow.value += ' ';
        }
        const boxSizing = computedStyle.boxSizing;
        const padding = getStyleValue(computedStyle.paddingBottom) +
            getStyleValue(computedStyle.paddingTop);
        const border = getStyleValue(computedStyle.borderBottomWidth) +
            getStyleValue(computedStyle.borderTopWidth);
        // The height of the inner content
        const innerHeight = inputShallow.scrollHeight;
        // Measure height of a textarea with a single row
        inputShallow.value = 'x';
        const singleRowHeight = inputShallow.scrollHeight;
        // The height of the outer content
        let outerHeight = innerHeight;
        if (minRows) {
            outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
        }
        if (maxRows) {
            outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
        }
        outerHeight = Math.max(outerHeight, singleRowHeight);
        // Take the box sizing into account for applying this value as a style.
        const outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0);
        const overflowing = Math.abs(outerHeight - innerHeight) <= 1;
        return { outerHeightStyle, overflowing };
    }, [maxRows, minRows, props.placeholder]);
    const syncHeight = React.useCallback(() => {
        const textareaStyles = calculateTextareaStyles();
        if (isEmpty(textareaStyles)) {
            return;
        }
        const outerHeightStyle = textareaStyles.outerHeightStyle;
        const input = inputRef.current;
        if (heightRef.current !== outerHeightStyle) {
            heightRef.current = outerHeightStyle;
            input.style.height = `${outerHeightStyle}px`;
        }
        input.style.overflow = textareaStyles.overflowing ? 'hidden' : '';
    }, [calculateTextareaStyles]);
    useEnhancedEffect(() => {
        const handleResize = () => {
            syncHeight();
        };
        // Workaround a "ResizeObserver loop completed with undelivered notifications" error
        // in test.
        // Note that we might need to use this logic in production per https://github.com/WICG/resize-observer/issues/38
        // Also see https://github.com/mui/mui-x/issues/8733
        let rAF;
        const rAFHandleResize = () => {
            cancelAnimationFrame(rAF);
            rAF = requestAnimationFrame(() => {
                handleResize();
            });
        };
        const debounceHandleResize = debounce(handleResize);
        const input = inputRef.current;
        const containerWindow = ownerWindow(input);
        containerWindow.addEventListener('resize', debounceHandleResize);
        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(process.env.NODE_ENV === 'test' ? rAFHandleResize : handleResize);
            resizeObserver.observe(input);
        }
        return () => {
            debounceHandleResize.clear();
            cancelAnimationFrame(rAF);
            containerWindow.removeEventListener('resize', debounceHandleResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [calculateTextareaStyles, syncHeight]);
    useEnhancedEffect(() => {
        syncHeight();
    });
    const handleChange = (event) => {
        if (!isControlled) {
            syncHeight();
        }
        if (onChange) {
            onChange(event);
        }
    };
    return (_jsxs(React.Fragment, { children: [_jsx("textarea", { value: value, onChange: handleChange, ref: handleRef, 
                // Apply the rows prop to get a "correct" first SSR paint
                rows: minRows, style: style, ...other }), _jsx("textarea", { "aria-hidden": true, className: props.className, readOnly: true, ref: shadowRef, tabIndex: -1, style: {
                    ...styles.shadow,
                    ...style,
                    paddingTop: 0,
                    paddingBottom: 0,
                } })] }));
};
//# sourceMappingURL=textarea-autosize.js.map